import React, { useRef, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";

// React 17 has no useId, so ids for the description below are minted here.
// A module counter is enough: this component never server-renders, so there is
// no client/server id mismatch to worry about.
let idSeq = 0;

/**
 * A password field with a reveal toggle.
 *
 * WHY THIS IS A COMPONENT AND NOT ANOTHER COPY
 * --------------------------------------------
 * Eight screens had already grown their own reveal toggle (Login,
 * PartnerActivate, VendorRegistrationForm, PasswordResetForm,
 * AgentPasswordResetForm, MobileClientPasswordResetForm, Restaurants,
 * VendorWallet) and they had drifted into three different controls: a
 * <button>, a Material-UI <IconButton>, and, in VendorRegistrationForm, a
 * bare <span>, which no keyboard can reach and no screen reader announces, so
 * on that form the reveal simply does not exist for anyone not using a mouse.
 * Two icon sets between them, and several pin the control out of the tab order
 * with tabIndex={-1}, which has the same effect on purpose.
 *
 * So: one implementation, a real <button> that is focusable and labelled, and
 * nothing about the look hard-coded.
 *
 * `type="button"` below is load-bearing, not decorative: a <button> with no
 * type defaults to submit, and every one of these toggles sits inside a
 * <form>. The existing copies all remembered it. A shared component is where
 * that stops depending on remembering.
 *
 * STYLE-AGNOSTIC ON PURPOSE
 * -------------------------
 * Screens in this dashboard style inputs three different ways (inline style
 * objects via `inp()`, `className="form-control"`, reactstrap <Input>). This
 * renders a plain <input> and spreads every prop it does not use onto it, so
 * all three callers keep the look they already have. It only ever adds the
 * right-hand padding the icon needs, on top of whatever `style` it was given.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * --------------------------------
 * It holds no value of its own. Reveal state is UI state; the password
 * belongs to the caller's form. And it starts hidden on every mount, so a
 * revealed password can never survive a re-render, a step change or a
 * navigation back into the form.
 */
const PasswordInput = ({
  style,
  wrapperStyle,
  iconSize = 15,
  disabled,
  ...rest
}) => {
  const [revealed, setRevealed] = useState(false);
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `pwd-hint-${++idSeq}`;
  const describedBy = idRef.current;

  // The icon sits inside the field, so the text needs room to stop before it.
  // Added to the caller's padding rather than replacing it, so a field with
  // unusual padding keeps it.
  const padded = {
    ...style,
    paddingRight: 38,
  };

  return (
    <div style={{ position: "relative", display: "block", ...wrapperStyle }}>
      <input
        {...rest}
        disabled={disabled}
        type={revealed ? "text" : "password"}
        style={padded}
      />
      <button
        // Without this, a click inside a <form> submits it. See the note above.
        type="button"
        onClick={() => setRevealed((v) => !v)}
        disabled={disabled}
        aria-label={revealed ? "Hide password" : "Show password"}
        aria-pressed={revealed}
        aria-describedby={describedBy}
        title={revealed ? "Hide password" : "Show password"}
        style={{
          position: "absolute",
          right: 1,
          top: 0,
          height: "100%",
          width: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          border: "none",
          background: "transparent",
          color: disabled ? "#D1D5DB" : revealed ? "#006202" : "#9CA3AF",
          cursor: disabled ? "not-allowed" : "pointer",
          borderRadius: 8,
          lineHeight: 0,
        }}
      >
        {revealed ? <FaEyeSlash style={{ fontSize: iconSize }} /> : <FaEye style={{ fontSize: iconSize }} />}
      </button>
      {/*
        Screen readers announce the button's label, but not the consequence.
        Anyone on a shared screen deserves to know the characters become
        visible before they press it.
      */}
      <span
        id={describedBy}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
        }}
      >
        Shows the password in plain text on screen
      </span>
    </div>
  );
};

export default PasswordInput;
